import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { SwapHorizontalCircleOutlined } from '@mui/icons-material'
import CircularProgress from '@mui/material/CircularProgress'
import React, { useEffect, useRef, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Client, IMessage } from '@stomp/stompjs'
import { useTranslation } from 'react-i18next'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import SockJS from 'sockjs-client'

import { fetchCooperatingWholesalersData } from 'shared/services/fetchCooperationWholesalers'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import mergeObjects from 'shared/utils/helpers/mergeObjects'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import { useProfileQuery } from 'shared/store/useProfile'
import useLogin from 'shared/store/useLogin'
import { UserRoles } from 'shared/consts'
import { httpClient } from 'shared/lib'

import NoCooperationTable from './components/NoCooperationTable'
import NoProductTable from './components/NoProductTable'
import ProductInfo from './components/ProductInfo'
import { fetchProductDetails, formatExternalAvailability } from './services'
import MainTable from './components/MainTable'
import { FormatResponsePayload, ProductDetails } from './types'
import { pl, en, de } from './locales'

const SOCKET_URL = `${process.env.REACT_APP_API_URL}/websockets`

export default function ProductDetailsView() {
   const { i18n, t } = useTranslation('PRODUCT_DETAILS')
   i18n.addResourceBundle('pl-PL', 'PRODUCT_DETAILS', pl)
   i18n.addResourceBundle('en-EN', 'PRODUCT_DETAILS', en)
   i18n.addResourceBundle('de-DE', 'PRODUCT_DETAILS', de)
   const [isData, setIsData] = useState(false)
   const [productNotFound, setProductNotFound] = useState(false)
   const [productDetails, setProductDetails] = useState<ProductDetails>()
   const { user } = useLogin()
   const [data, setData] = useState<FetchCooperationDataResult>({
      availableWholesalers: [],
      unAvailableWholesalers: [],
   })
   const { data: userProfileData } = useProfileQuery()
   const websocket = useRef<Client>(null)
   const location = useLocation()
   const [searchParams] = useSearchParams()
   const navigate = useNavigate()
   const productID = searchParams.get('uuid')

   function publishWithPromise(destination: string, headers: Record<string, string>, body: string): Promise<void> {
      return new Promise((resolve) => {
         if (websocket.current) {
            websocket.current.publish({ destination, headers, body })
         }
         resolve()
      })
   }

   async function handleWebSocketMessage(msg: IMessage): Promise<void> {
      if (JSON.parse(msg.body).type && JSON.parse(msg.body).type === 'END') {
         if (websocket.current) {
            websocket.current.deactivate()
         }
      } else {
         setData((prevData) => {
            const newData = {
               ...prevData,
               availableWholesalers: prevData.availableWholesalers.map((item) => {
                  if (msg.body) {
                     const jsonBody = JSON.parse(msg.body) as CooperatingWholesaler
                     if (item.wholesaleUuid === jsonBody.wholesaleUuid) {
                        const updatedItem = mergeObjects(item, jsonBody) as CooperatingWholesaler
                        return updatedItem
                     }
                  }
                  return item
               }),
            }
            return newData
         })
      }
   }

   async function handleExternalAvailability(wholesaler: CooperatingWholesaler) {
      const wholesalerInfo = data.availableWholesalers.find((item) => item.wholesaleUuid === wholesaler.wholesaleUuid)

      if (wholesalerInfo?.wholesaleIndex === wholesaler.wholesaleIndex) {
         setData((prevData) => {
            const newData = {
               ...prevData,
               availableWholesalers: prevData.availableWholesalers.map((item) => {
                  if (
                     item.wholesaleUuid === wholesaler.wholesaleUuid &&
                     item.status !== 'OK_ONLINE_AVAILABILITY' &&
                     item.status !== 'ONLINE_CONNECTION_PROBLEM'
                  ) {
                     const updatedItem = mergeObjects(item, wholesaler) as CooperatingWholesaler
                     return updatedItem
                  }
                  return item
               }),
            }
            return newData
         })
      }
   }

   async function onConnected(): Promise<void> {
      if (websocket.current) {
         await publishWithPromise(
            `/core/api/ws/product/availability-in-cooperating/${userProfileData?.uuid}${productID}`,
            {
               'X-Authorization': `Bearer ${window.localStorage.getItem('ACCESS_TOKEN')}`,
            },
            JSON.stringify({ productUuid: productID })
         )

         await new Promise<void>((resolve) => {
            if (websocket.current) {
               websocket.current.subscribe(
                  `/product/availability-in-cooperating/${userProfileData?.uuid}${productID}`,
                  (msg) => {
                     handleWebSocketMessage(msg)
                     resolve()
                  }
               )
            }
         })
      }
   }

   async function init(productUuid: string) {
      await fetchProductDetails(productUuid)
         .then((res) => setProductDetails(res))
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               if (error.response?.data.errors[0].code === 'errors.product.not-found') setProductNotFound(true)
            }
         })
      await fetchCooperatingWholesalersData(productUuid).then((res) => {
         setData({
            ...data,
            availableWholesalers: res.availableWholesalers,
            unAvailableWholesalers: res.unAvailableWholesalers,
         })
         setIsData(true)
      })
   }

   useEffect(() => {
      if (!productID) {
         navigate('/dashboard/search')
         return
      }
      if (userProfileData) {
         init(productID)
      }
   }, [productID, userProfileData, httpClient])

   useEffect(() => {
      setTimeout(async () => {
         await fetchProductDetails(productID!)
            .then((res) => setProductDetails(res))
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  if (error.response?.data.errors[0].code === 'errors.product.not-found') {
                     setProductNotFound(true)
                  } else {
                     navigate('/dashboard/error-page', {
                        state: {
                           variant: 'backend',
                           path: location.pathname,
                           error: {
                              message: error.response?.data.errors[0].code ?? '',
                              stack: 'unknown',
                           },
                           props: {
                              productUuid: productID!,
                           },
                        },
                     })
                  }
               }
            })
      }, 500)
   }, [i18n.language])

   useEffect(() => {
      if (localStorage.getItem('searchBlocked') === 'SUBSCRIPTION_DOWNGRADE') {
         if (user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN)) {
            navigate('/dashboard/wholesales-keeping')
         } else {
            navigate('/dashboard')
         }
      }
      if (localStorage.getItem('searchBlocked') === 'MISSING_PAYMENT') {
         navigate('/dashboard')
      }
   }, [localStorage.getItem('searchBlocked')])

   useEffect(() => {
      if (isData) {
         websocket.current = new Client({
            webSocketFactory: () => new SockJS(SOCKET_URL),
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: onConnected,
            // onDisconnect: () => console.log(0, 'WS IS DISCONNECTED'),
            connectHeaders: {
               'X-Authorization': `Bearer ${window.localStorage.getItem('ACCESS_TOKEN')}`,
            },
            // onStompError: (err: IFrame) => console.log(0, 'STOMP ERROR', err),
            // debug: (status: string) => console.log(0, 'WS DEBUG STATUS', status),
         })
         websocket.current.activate()
      }
   }, [productID, isData, userProfileData])

   useEffect(() => {
      return () => {
         if (websocket.current) {
            websocket.current.deactivate()
         }
      }
   }, [])

   useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
         if (event.data?.type === 'EXTENSION_DATA') {
            const details = event.data.payload
            // console.log('📦 Received data from extension:', details, productDetails?.index)
            if (
               data.availableWholesalers.find(
                  (item) =>
                     item.wholesaleIndex === details.wholesaleIndex ||
                     productDetails?.index === details.wholesaleIndex.replace(/\s/g, '')
               )
            ) {
               if (
                  data.availableWholesalers.find((item) => item.wholesaleUuid === details.wholesaleUuid) &&
                  !data.availableWholesalers.find((item) => item.wholesaleUuid === details.wholesaleUuid)?.netPrice
               ) {
                  formatExternalAvailability(details as FormatResponsePayload, productID!).then((res) => {
                     handleExternalAvailability(res.data)
                  })
               }
            }
         }
      }

      window.addEventListener('message', handleMessage)
      return () => window.removeEventListener('message', handleMessage)
   }, [data])

   return (
      <Box
         sx={{
            display: 'flex',
            flexDirection: 'column',
         }}
      >
         {productNotFound ? (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
               <Typography variant="h4" color="primary">
                  {t('productNotFound')}
               </Typography>
            </div>
         ) : (
            <>
               <ProductInfo data={productDetails} />
               {isData ? (
                  <MainTable data={data.availableWholesalers} details={productDetails} productID={productID} />
               ) : (
                  <Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
                     <CircularProgress />
                  </Box>
               )}
               {!user.roles.includes(UserRoles.DEMO) && !user.roles.includes(UserRoles.DEMO_SUBSCRIPTION) && (
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: 2,
                        pr: { lg: 0, md: 0, xs: '20px' },
                        width: '100%',
                     }}
                  >
                     {!user.roles.includes(UserRoles.DEMO) && !user.roles.includes(UserRoles.DEMO_SUBSCRIPTION) && (
                        <Badge badgeContent="BETA" color="error">
                           <ContainedButton
                              onClick={() =>
                                 navigate({
                                    pathname: '/dashboard/product/replacements',
                                    search: `?${createSearchParams({
                                       uuid: productID!,
                                    })}`,
                                 })
                              }
                              variant="contained"
                           >
                              <SwapHorizontalCircleOutlined sx={{ color: 'white', marginRight: 2 }} />
                              {t('PRODUCT_DETAILS:tables.showNewSubstitutesButton')}
                           </ContainedButton>
                        </Badge>
                     )}
                     {/* <Badge badgeContent="BETA" color="error">
                        <ContainedButton
                           sx={{ ml: '24px' }}
                           onClick={() =>
                              navigate({
                                 pathname: '/dashboard/search',
                                 search: `?${createSearchParams({
                                    value: productID!,
                                    index: productDetails!.index!,
                                    searchType: 'SUBSTITUTES',
                                 })}`,
                              })
                           }
                           variant="contained"
                        >
                           <SwapHorizontalCircleOutlined sx={{ color: 'white', marginRight: 2 }} />
                           {t('PRODUCT_DETAILS:tables.showSubstitutesButton')}
                        </ContainedButton>
                     </Badge> */}
                  </Box>
               )}
               {isData && <NoCooperationTable locationID={userProfileData?.locationUuid} productID={productID} />}
               <NoProductTable data={data.unAvailableWholesalers} productID={productID} />
            </>
         )}
      </Box>
   )
}
